import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Box,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  LinearProgress,
  Avatar,
  Grid,
  alpha,
} from '@mui/material';
import {
  MoreVert,
  Tv,
  Monitor,
  Tablet,
  Lightbulb,
  ViewModule,
  Wifi,
  WifiOff,
  Error,
  Build,
  Refresh,
  Update,
  Assignment,
  PlaylistPlay,
  Settings,
  Info,
  PowerSettingsNew,
  SignalWifi4Bar,
  SignalWifi0Bar,
  Memory,
  Storage,
  // Новые иконки (4 августа 2025)
  CameraAlt,
  PlayArrow,
  ControlCamera,
  CalendarMonth,
} from '@mui/icons-material';

import type { Device } from '../../types';
import { DeviceType, DeviceStatus } from '../../types';

interface DeviceCardProps {
  device: Device;
  onRestart?: (device: Device) => void;
  onUpdate?: (device: Device) => void;
  onViewLogs?: (device: Device) => void;
  onChangePlaylist?: (device: Device) => void;
  onEdit?: (device: Device) => void;
  onDelete?: (device: Device) => void;
  onViewDetails?: (device: Device) => void;
  // Новые обработчики (4 августа 2025)
  onViewScreenshots?: (device: Device) => void;
  onPlaybackControls?: (device: Device) => void;
  onViewAvailability?: (device: Device) => void;
}

const DeviceCard: React.FC<DeviceCardProps> = ({
  device,
  onRestart,
  onUpdate,
  onViewLogs,
  onChangePlaylist,
  onEdit,
  onDelete,
  onViewDetails,
  // Новые обработчики (4 августа 2025)
  onViewScreenshots,
  onPlaybackControls,
  onViewAvailability,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const getDeviceTypeIcon = (type?: string) => {
    switch (type) {
      case DeviceType.TV:
      case 'tv':
        return Tv;
      case DeviceType.MONITOR:
      case 'monitor':
        return Monitor;
      case DeviceType.TABLET:
      case 'tablet':
        return Tablet;
      case DeviceType.LED_PANEL:
      case 'led_panel':
        return Lightbulb;
      case DeviceType.VIDEO_WALL:
      case 'video_wall':
        return ViewModule;
      default:
        return Monitor;
    }
  };

  const typeLabels: Record<string, string> = {
    tv: 'Телевизор',
    monitor: 'Монитор',
    tablet: 'Планшет',
    led_panel: 'LED панель',
    video_wall: 'Видеостена',
    desktop_pc: 'ПК',
    raspberry_pi: 'Raspberry Pi',
    intel_nuc: 'Intel NUC',
    android_tv: 'Android TV',
    web_browser: 'Web Player',
    other: 'Другое',
  };

  const systemInfo = {
    ...(device.device_settings || {}),
    ...(device.system_info || {}),
  } as Record<string, any>;

  const inferPhysicalType = () => {
    const explicitType = String(device.device_type || device.type || '').toLowerCase();
    if (explicitType && explicitType !== 'web_browser' && explicitType !== 'other' && explicitType !== 'unknown') {
      return explicitType;
    }
    const name = `${device.name || ''} ${device.hostname || ''} ${device.location || ''} ${device.room || ''}`.toLowerCase();
    const osName = String(systemInfo.os_name || device.os_version || '').toLowerCase();
    const userAgent = String(systemInfo.user_agent || '').toLowerCase();
    if (/(^|\b)(tv|телевизор|телевиз|screen|экран|display)(\b|$)/i.test(name)) return 'tv';
    if (name.includes('tablet') || name.includes('планш')) return 'tablet';
    if (osName.includes('android') || userAgent.includes('android')) return 'android_tv';
    if (osName.includes('windows') || osName.includes('mac') || osName.includes('linux')) return 'desktop_pc';
    return 'monitor';
  };

  const getClientLabel = () => {
    const deviceType = String(device.device_type || device.type || '').toLowerCase();
    const player = String(systemInfo.player || systemInfo.player_kind || systemInfo.client_kind || '').toLowerCase();
    const cacheSource = String(systemInfo.cache_status?.source || '').toLowerCase();
    const userAgent = String(systemInfo.user_agent || '').toLowerCase();
    if (systemInfo.client_label) return String(systemInfo.client_label);
    if (player.includes('native-android') || player.includes('native_android') || player.includes('android_apk') || cacheSource.includes('android')) return 'Android APK';
    if (player.includes('desktop-python') || player.includes('desktop_python') || cacheSource.includes('desktop-python')) return 'Desktop Python';
    if (player.includes('electron') || userAgent.includes('electron')) return 'Windows Electron';
    if (deviceType === 'web_browser' || player.includes('web')) return 'Browser Web';
    return 'Device API';
  };

  const getStatusColor = (status: DeviceStatus) => {
    switch (status) {
      case DeviceStatus.ONLINE:
        return 'success';
      case DeviceStatus.OFFLINE:
        return 'error';
      case DeviceStatus.ERROR:
        return 'error';
      case DeviceStatus.MAINTENANCE:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: DeviceStatus) => {
    switch (status) {
      case DeviceStatus.ONLINE:
        return Wifi;
      case DeviceStatus.OFFLINE:
        return WifiOff;
      case DeviceStatus.ERROR:
        return Error;
      case DeviceStatus.MAINTENANCE:
        return Build;
      default:
        return WifiOff;
    }
  };

  const getSignalIcon = () => {
    if (device.signal_strength >= 75) return SignalWifi4Bar;
    if (device.signal_strength >= 25) return SignalWifi4Bar;
    return SignalWifi0Bar;
  };

  const formatLastSeen = (lastSeen: string): string => {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return t('devices.justNow');
    if (diffMins < 60) return t('devices.minutesAgo', { count: diffMins });
    if (diffMins < 1440) return t('devices.hoursAgo', { count: Math.floor(diffMins / 60) });
    return t('devices.daysAgo', { count: Math.floor(diffMins / 1440) });
  };

  const formatUptime = (uptime: number): string => {
    const days = Math.floor(uptime / 86400);
    const hours = Math.floor((uptime % 86400) / 3600);
    const minutes = Math.floor((uptime % 3600) / 60);
    
    if (days > 0) return t('devices.uptimeDays', { days, hours });
    if (hours > 0) return t('devices.uptimeHours', { hours, minutes });
    return t('devices.uptimeMinutes', { minutes });
  };

  const physicalType = inferPhysicalType();
  const DeviceTypeIcon = getDeviceTypeIcon(physicalType);
  const StatusIcon = getStatusIcon(device.status);
  const SignalIcon = getSignalIcon();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      {/* Заголовок карточки */}
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          {/* Аватар с иконкой типа устройства */}
          <Avatar
            sx={{
              bgcolor: device.status === DeviceStatus.ONLINE ? 'success.main' : 'grey.400',
              width: 48,
              height: 48,
            }}
          >
            <DeviceTypeIcon />
          </Avatar>

          {/* Основная информация */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {device.name}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                icon={<StatusIcon fontSize="small" />}
                label={t(`devices.status.${device.status.toLowerCase()}`)}
                color={getStatusColor(device.status)}
                size="small"
                variant="outlined"
              />
              <Chip label={typeLabels[physicalType] || 'Экран'} size="small" variant="outlined" />
              <Chip label={getClientLabel()} size="small" color="info" variant="outlined" />
            </Box>

            {device.location && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                📍 {device.location}
              </Typography>
            )}
          </Box>

          {/* Меню действий */}
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Box>

        {/* Детальная информация */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SignalIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {device.ip_address}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              {formatLastSeen(device.last_heartbeat)}
            </Typography>
          </Grid>
        </Grid>

        {/* Текущее воспроизведение */}
        {(device.current_media || device.current_playlist) && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <PlaylistPlay fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Сейчас играет
              </Typography>
            </Box>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={device.current_media?.title || device.current_playlist?.name}
            >
              {device.current_media?.title || device.current_playlist?.name}
            </Typography>
            {device.current_playlist?.name && device.current_media?.title && (
              <Typography variant="caption" color="text.secondary">
                {device.current_playlist.name}
              </Typography>
            )}
          </Box>
        )}

        {/* Упрощенная системная информация */}
        {device.system_info && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {device.system_info.cpu_usage !== undefined && (
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">CPU</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {device.system_info.cpu_usage}%
                  </Typography>
                </Box>
              </Grid>
            )}
            {device.system_info.memory_usage !== undefined && (
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">RAM</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {device.system_info.memory_usage}%
                  </Typography>
                </Box>
              </Grid>
            )}
            {device.system_info.storage_usage !== undefined && (
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">HDD</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {device.system_info.storage_usage}%
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {/* Uptime */}
        {device.uptime && (
          <Typography variant="caption" color="text.secondary">
            ⏱️ {formatUptime(device.uptime)}
          </Typography>
        )}
      </CardContent>

      {/* Действия */}
      <CardActions sx={{ mt: 'auto', px: 2, pb: 2 }}>
        <Button
          size="small"
          onClick={() => onViewDetails?.(device)}
          startIcon={<Info />}
        >
          {t('common.details')}
        </Button>

        {device.status === DeviceStatus.ONLINE && onChangePlaylist && (
          <Button
            size="small"
            onClick={() => onChangePlaylist(device)}
            startIcon={<PlaylistPlay />}
          >
            {t('devices.changePlaylist')}
          </Button>
        )}
      </CardActions>

      {/* Контекстное меню */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {onViewDetails && (
          <MenuItem onClick={() => handleAction(() => onViewDetails(device))}>
            <ListItemIcon>
              <Info fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('devices.viewDetails')}</ListItemText>
          </MenuItem>
        )}

        {onEdit && (
          <MenuItem onClick={() => handleAction(() => onEdit(device))}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit')}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {onRestart && device.status === DeviceStatus.ONLINE && (
          <MenuItem onClick={() => handleAction(() => onRestart(device))}>
            <ListItemIcon>
              <Refresh fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('devices.restart')}</ListItemText>
          </MenuItem>
        )}

        {onUpdate && (
          <MenuItem onClick={() => handleAction(() => onUpdate(device))}>
            <ListItemIcon>
              <Update fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('devices.commands.update')}</ListItemText>
          </MenuItem>
        )}

        {onChangePlaylist && device.status === DeviceStatus.ONLINE && (
          <MenuItem onClick={() => handleAction(() => onChangePlaylist(device))}>
            <ListItemIcon>
              <PlaylistPlay fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('devices.changePlaylist')}</ListItemText>
          </MenuItem>
        )}

        {onViewLogs && (
          <MenuItem onClick={() => handleAction(() => onViewLogs(device))}>
            <ListItemIcon>
              <Assignment fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('devices.logs.viewLogs')}</ListItemText>
          </MenuItem>
        )}

        {onViewAvailability ? (
          <MenuItem onClick={() => handleAction(() => onViewAvailability(device))}>
            <ListItemIcon>
              <CalendarMonth fontSize="small" />
            </ListItemIcon>
            <ListItemText>Календарь доступности</ListItemText>
          </MenuItem>
        ) : null}

        {onViewScreenshots ? (
          <MenuItem onClick={() => handleAction(() => onViewScreenshots(device))}>
            <ListItemIcon>
              <CameraAlt fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('devices.screenshots.title')}</ListItemText>
          </MenuItem>
        ) : null}

        {onPlaybackControls ? (
          <MenuItem onClick={() => handleAction(() => onPlaybackControls(device))}>
            <ListItemIcon>
              <ControlCamera fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('devices.playback.title')}</ListItemText>
          </MenuItem>
        ) : null}

        {onDelete && [
          <Divider key="divider" />,
          <MenuItem key="delete" onClick={() => handleAction(() => onDelete(device))}>
            <ListItemIcon>
              <PowerSettingsNew fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
        ]}
      </Menu>
    </Card>
  );
};

export default DeviceCard;
